import React, {useEffect, useMemo, useState} from 'react';
import VStack from "../../../../../common/Layout/VStack";
import BasicInformation from "./BasicInformation";
import {getPriceAfterSubsidy, handlePricingWithCurrency, isBoxInTrial, isMobile} from "../../../../../../services/Helpers";
import {fetchApi} from "../../../../../../services/HTTP";
import Box from "../../../../../common/Layout/Box";
import AppText from "../../../../../common/AppText";
import {GlobalStyleAttributes} from "../../../../../../styles/GlobalStyles";
import {Divider} from "antd";
import {t} from "../../../../../../services/i18n";
import AvailabilitySlotSelection from "./AvailabilitySlotSelection";
import RepeatConfiguration from "./RepeatConfiguration";
import AdvanceSelect from "../../../../../common/AdvanceSelect";
import FormInput from "../../../../../common/FormInput";
import {USERS_SELECT} from "../../../../../../services/ConstHelper";
import CustomButtonV2 from "../../../../../common/CustomButtonV2";
import {CREATE_USER_MODAL, openModal, updateModalRes} from "../../../../../../redux/modalManagerSlice";
import {Colors} from "../../../../../../styles/Colors";
import ProTipBox from "../../../../../common/ProTipBox";
import {useDispatch, useSelector} from "react-redux";
import {addToSelectionsTree} from "../../../../../../redux/stepperSlice";
import {useNavigate} from "react-router-dom";

const BasicInfoAppointment = (props) => {
    const { values, setFieldValue, setValues, errors, touched, validationObject, target, fromAvailability, oneTimeMembership, ...additional} = props;
    const selectionTree = useSelector(state => state.stepper.selectionsTree)
    const createUserModalRes = useSelector(state => state.modalManager.createUserModal.processRes)
    const editType = useSelector((state) => state.stepper.editType);
    const box = useSelector(state => state.box.box)
    const selectedLocation = useSelector(state => state.userSettings.scheduleLocation)
    const dispatch = useDispatch()
    const navigate = useNavigate();
    const currencySymbol = useMemo(() => selectedLocation ? selectedLocation.currency_symbol : '', [selectedLocation])
    const [fundingOrganizations, setFundingOrganizations] = useState([])
    const showFundingBodySelect = !!box?.organizations_support && !!oneTimeMembership && fundingOrganizations.length > 0 && !editType
    const selectedUnion = values.scheduleUser?.union

    useEffect(() => {
        if (!oneTimeMembership?.id || !box?.organizations_support) {
            setFundingOrganizations([])
            setFieldValue('scheduleUser.union', null)
            return
        }
        const selectedUnionId = values.scheduleUser?.union?.users_boxes_id
        fetchApi(`organizations/subsidies/${oneTimeMembership.id}`, 'get', null, false, true)
            .then((rows) => {
                const list = rows ?? []
                setFundingOrganizations(list)
                setFieldValue('scheduleUser.union', selectedUnionId ? list.find(o => o.users_boxes_id === selectedUnionId) ?? null : null)
            })
            .catch(() => {
                setFundingOrganizations([])
                setFieldValue('scheduleUser.union', null)
            })
    }, [oneTimeMembership?.id, box?.organizations_support])

    useEffect(() => {
        console.log(values, errors)
    }, [values, errors]);

    useEffect(() => {
        if(createUserModalRes) {
            onUserSelected({...createUserModalRes, label: createUserModalRes.full_name, membership_user: []})
            dispatch(updateModalRes({modalName: CREATE_USER_MODAL, res: null}))
        }
    }, [createUserModalRes]);

    useEffect(() => {
        if(values?.waitlist_id && values?.bookedUser && !selectionTree?.user) {
            onUserSelected(values.bookedUser)
        }
    }, [values?.waitlist_id, values?.bookedUser, selectionTree?.user]);

    const getFundingBodyBreakdown = () => {
        if (!selectedUnion) return null
        const subsidy = handlePricingWithCurrency(selectedUnion.subsidy?.toLocaleString(), currencySymbol)
        const customerPays = handlePricingWithCurrency(getPriceAfterSubsidy(oneTimeMembership?.price, selectedUnion)?.toLocaleString(), currencySymbol)
        if (selectedUnion.union_price != null && selectedUnion.union_price !== oneTimeMembership?.price) {
            const unionPrice = handlePricingWithCurrency(selectedUnion.union_price?.toLocaleString(), currencySymbol)
            return t(`appointment-funding-body-breakdown-union-price${isMobile ? '-mobile' : ''}`, {unionPrice, subsidy, customerPays})
        }
        return t(`appointment-funding-body-breakdown-subsidy${isMobile ? '-mobile' : ''}`, {subsidy, customerPays})
    }

    const fundingBodyBreakdown = useMemo(() => getFundingBodyBreakdown(), [selectedUnion, oneTimeMembership?.price, currencySymbol])

    const onUserSelected = (user) => {
        setValues({
            ...values,
            scheduleUser: {
                ...(values.scheduleUser ?? {}),
                user_fk: user?.user_fk,
                membership_user_fk: null,
                isLead: user?.type === 'lead',
                isNoMembership: false,
                isUserActive: user?.active
            }
        })
        const memberships = user?.membership_user
            .filter(item => !((item.sessions_left === 0 && user?.active) && item.membership_types.type === 'session'))
            .filter(item => user.active ? item.active : !item.active)
        const newUser = user ? {...user, membership_user: memberships} : null

        dispatch(addToSelectionsTree({user: newUser}))
    }

    return (
        <VStack space={'1rem'}>
            <VStack space={'1rem'}>
                <AppText fontSize={GlobalStyleAttributes.fonts.heading2Mobile} boldFont>{t('basic-info-section-header')}</AppText>
                <Divider style={{ margin: 0 }}/>
                <Box style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '1rem' }}>
                    {(editType || values.waitlist_id) ?
                        <VStack space={'0.2rem'} w={'100%'}>
                            <AppText fontSize={GlobalStyleAttributes.fonts.infoText} mediumFont>{t('select-client')}*</AppText>
                            <AdvanceSelect options={[{...values.bookedUser, label: values.bookedUser?.full_name, value: values.bookedUser?.user_fk}]} initValue={values.bookedUser?.user_fk} isDisabled={true}/>
                        </VStack>
                        :
                        <VStack space={'0.2rem'} w={'100%'}>
                            <AppText fontSize={GlobalStyleAttributes.fonts.infoText} mediumFont>{t('select-client')}*</AppText>
                            <AdvanceSelect initValue={values.scheduleUser?.user_fk}
                                           type={USERS_SELECT}
                                           onChange={onUserSelected}
                                           value={selectionTree['user']}
                                           isClearable={true}
                                           hideFreefitType
                                           hideCustomerId={isMobile}
                                           allowInactive
                                           getInactiveMemberships
                                           placeholder={t('appointment-user-select-placeholder')}
                                           smallSelectionDesign
                                           showSelectedUserWithStatus
                                           isError={!!(touched.scheduleUser?.user_fk && errors.scheduleUser?.user_fk)}
                            />
                            <CustomButtonV2 onClick={() => dispatch(openModal({modalName: CREATE_USER_MODAL, props: {navigate, viaFunnel: true, initialValues: {locations_box_fk: values.locations_box_fk}, displayProps: {showPersonalId: true}}}))}
                                            text={`+ ${t('add-new-client')}`}
                                            applyHoverOnText={true}
                                            type={'link'}
                                            style={{justifyContent: 'start'}}
                                            iconProps={{iconColor: Colors.newDesign.mainBlue, iconHoverColor: Colors.newDesign.mainBlueHover}}
                            />
                            {isBoxInTrial() && <ProTipBox text={t('appointment-user-select-tip')}/>}
                        </VStack>
                    }
                    <BasicInformation withoutWrapper={true} {...props}/>
                </Box>
                {showFundingBodySelect &&
                    <FormInput label={t('appointment-funding-body-label')} width={isMobile ? '100%' : '33%'} containerProps={{ mb: 0 }} helper={fundingBodyBreakdown} helperProps={{ fontSize: GlobalStyleAttributes.fonts.standard16 }} errorHeight={'unset'}>
                        <AdvanceSelect
                            isClearable={false}
                            options={[{ value: 0, label: t('appointment-funding-body-private') }, ...fundingOrganizations.map(o => ({ value: o.users_boxes_id, label: o.name }))]}
                            initValue={selectedUnion?.users_boxes_id ?? 0}
                            onChange={(option) => setFieldValue('scheduleUser.union', fundingOrganizations.find(o => o.users_boxes_id === option?.value) ?? null)}
                        />
                    </FormInput>
                }
            </VStack>
            <VStack space={'1rem'}>
                <AppText fontSize={GlobalStyleAttributes.fonts.heading2Mobile} boldFont>{t('timing-section-header')}</AppText>
                <Divider style={{ margin: 0 }}/>
                {
                    fromAvailability ?
                        <AvailabilitySlotSelection {...props}/>
                        :
                        <RepeatConfiguration {...props}/>
                }
            </VStack>
        </VStack>
    );
};

export default BasicInfoAppointment;
